import { ChartPieSlice, Coins, ShieldCheck, TrendUp } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

function DistributionRow({ icon: Icon, name, roleLabel, dotClassName, amount, pct }) {
  return (
    <div className="flex items-center gap-space-2 border-t border-border py-space-3 first:border-t-0">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-sm bg-sage-tint text-sage-on-tint">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="type-display-row font-medium text-foreground">{name}</span>
        <span className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', dotClassName)} aria-hidden />
          <span className="type-chip font-semibold text-muted-foreground">{roleLabel}</span>
        </span>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
        <span className="type-display-row font-numeric font-semibold text-foreground">
          {formatEUR(amount)}
        </span>
        <span className="type-label font-normal text-muted-foreground">
          {pt.emergencyFund.dashboard.capital.rowPct.replace('{pct}', String(pct))}
        </span>
      </div>
    </div>
  );
}

/** .pen `ds/card/composition-group` (instância "distribuição do teu capital"). */
export function EmergencyFundCapitalDistribution({
  efTotal,
  investedTotal,
  idleTotal,
  avgMonthlyExpenses,
  snapshotsUsed,
}) {
  const copy = pt.emergencyFund.dashboard.capital;
  const total = efTotal + investedTotal + idleTotal;
  if (total <= 0) return null;

  const pct = (value) => (total > 0 ? Math.round((value / total) * 100) : 0);
  const rows = [
    {
      key: 'ef',
      icon: ShieldCheck,
      name: copy.efSlice,
      roleLabel: copy.roleProtected,
      dotClassName: 'bg-capital-protected',
      segClassName: 'bg-capital-protected',
      amount: efTotal,
    },
    {
      key: 'invested',
      icon: TrendUp,
      name: copy.investmentSlice,
      roleLabel: copy.roleInvested,
      dotClassName: 'bg-capital-invested',
      segClassName: 'bg-capital-invested',
      amount: investedTotal,
    },
    {
      key: 'idle',
      icon: Coins,
      name: copy.savingsSlice,
      roleLabel: copy.roleIdle,
      dotClassName: 'bg-capital-idle',
      segClassName: 'bg-capital-idle',
      amount: idleTotal,
    },
  ].filter((row) => row.amount > 0);

  const hasAvg = Number(avgMonthlyExpenses) > 0 && Number(snapshotsUsed ?? 0) >= 3;

  return (
    <div className="overflow-hidden rounded-ds-lg border border-border bg-card shadow-sm dark:shadow-none">
      <div className="flex items-center gap-space-3 px-space-5 py-space-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-sm bg-sage-tint text-sage-on-tint">
          <ChartPieSlice className="h-4 w-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="type-title-card block text-foreground">{copy.title}</span>
          <span className="type-chip block font-normal text-muted-foreground">
            {copy.totalLabel} · {formatEUR(total)}
          </span>
        </span>
      </div>

      <div className="border-t border-border px-space-5 pb-space-4">
        <div className="flex h-2 w-full overflow-hidden rounded-ds-full">
          {rows.map((row) => (
            <div
              key={row.key}
              className={row.segClassName}
              style={{ width: `${pct(row.amount)}%` }}
            />
          ))}
        </div>

        <div className="mt-space-1">
          {rows.map((row) => (
            <DistributionRow
              key={row.key}
              icon={row.icon}
              name={row.name}
              roleLabel={row.roleLabel}
              dotClassName={row.dotClassName}
              amount={row.amount}
              pct={pct(row.amount)}
            />
          ))}
        </div>

        {hasAvg ? (
          <p className="type-chip mt-space-2 border-t border-border pt-space-2 font-normal text-muted-foreground">
            {copy.avgExpenses
              .replace('{count}', String(snapshotsUsed))
              .replace('{amount}', formatEUR(Number(avgMonthlyExpenses)))}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default EmergencyFundCapitalDistribution;
